import { useEffect } from 'react';
import styled from 'styled-components';
import { useCoreAuth } from '@carvana/core-auth';
import { breakpoint, heading, body, spacing, text } from '@carvana/ds-styles';
import Button, { NavigableButton } from '@carvana/ds-button';
import { hasMobileUserAgent } from '@carvana/stc-utilities';

import useAccountModal from '../../hooks/useAccountModal';
import { getBrowserData } from '../../utilities/browserDataExtractor';
import { requestCredentialsUpdate } from '../../utilities/mobileCredentials';
import { recordAction, recordActionImmediately } from '../../utilities/moMetrics/uiMetrics';
import Spacer from '../_shared/styles/spacer';

const ErrorWrapper = styled.div`
  align-content: middle;
  display: flex;
  flex-direction: column;
  flex: 1;
  justify-self: center;
  padding-bottom: 50px;
  padding-top: 50px;
  text-align: center;
`;

const ErrorHeading = styled.h1`
  ${heading.xl};
  color: ${text.default};
  text-align: center;
  width: 100%;

  @media screen and (min-width: ${breakpoint.sm}) {
    ${heading['2Xl']};
  }
`;

const ErrorDescription = styled.div`
  ${body.regular.md}
  color: ${text.default};
  margin-top: 8px;
`;

const Ctas = styled.div`
  display: flex;
  flex: 1;
  justify-content: center;
  gap: ${spacing.md};
  margin-top: 32px;
`;

const StyledNavigableButton = styled(NavigableButton)`
  box-sizing: border-box;
`;

type CtaButtonProps = {
  errorType: string;
  isLoggedIn: boolean;
  signIn: (callback: () => void) => void;
};

const CtaButton = ({ errorType, isLoggedIn, signIn }: CtaButtonProps) => {
  if (errorType === '404' || isLoggedIn) {
    return (
      <StyledNavigableButton
        onClick={async () => {
          await recordActionImmediately('ErrorPage', 'Sell Your Car', 'Clicked');
          window.location.href = '/sell-my-car';
        }}
      >
        Sell your car
      </StyledNavigableButton>
    );
  }

  const signInAction = async () => {
    await recordActionImmediately('ErrorPage', 'Sign In', 'Clicked');
    if (hasMobileUserAgent()) {
      await requestCredentialsUpdate();
    } else {
      signIn(() => window.location.reload());
    }
  };

  return (
    <Button variant="primary" onClick={signInAction}>
      Sign in
    </Button>
  );
};

type ErrorPageProps = {
  error?: string;
  header: string;
  subheader: string;
};

const ErrorPage = ({ error = '', header, subheader }: ErrorPageProps) => {
  const { signIn } = useAccountModal('Shell', 'ErrorPage');
  const { getUserState } = useCoreAuth();
  const { isLoggedIn, user } = getUserState();

  useEffect(() => {
    const browserData = getBrowserData();

    const customData = {
      requestedUrl: window.location.href,
      entryUrl: window.document.referrer,
      refererUrl: window.document.referrer,
      isAuthenticated: isLoggedIn,
      userId: user?.user_id || null,
      device: browserData.deviceCategory || browserData.deviceName || 'Unknown',
      browser: browserData.browserName
    };

    recordAction('ErrorPage', 'Landing', 'Rendered', customData);
  }, [isLoggedIn, user]);

  return (
    <Spacer>
      <ErrorWrapper>
        <ErrorHeading>{header}</ErrorHeading>
        <ErrorDescription>{subheader}</ErrorDescription>
        <Ctas>
          <CtaButton errorType={error} isLoggedIn={isLoggedIn} signIn={signIn} />
          <StyledNavigableButton
            variant="secondary"
            onClick={async () => {
              await recordActionImmediately('ErrorPage', 'Find A Car', 'Clicked');
              window.location.href = '/cars';
            }}
          >
            Find a car
          </StyledNavigableButton>
        </Ctas>
      </ErrorWrapper>
    </Spacer>
  );
};

export default ErrorPage;
